import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BookOpen, Plus, Edit3, Trash2, Power, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const EMPTY = { reference: '', text: '', category: 'Inspiration', is_active: true };

const CATEGORIES = ['Inspiration', 'Hope', 'Strength', 'Comfort', 'Wisdom', 'Faith', 'Love', 'Salvation', 'Courage', 'Worship'];

const BibleQuotesModule = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bible-quotes/all`);
      setQuotes(res.data || []);
    } catch (e) {
      toast.error('Failed to load Bible quotes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing('new');
    setForm(EMPTY);
  };

  const startEdit = (q) => {
    setEditing(q.id);
    setForm({ reference: q.reference, text: q.text, category: q.category || 'Inspiration', is_active: !!q.is_active });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.reference.trim() || form.text.trim().length < 4) {
      toast.error('Reference and verse text are required');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/bible-quotes`, form);
        toast.success('Quote added');
      } else {
        await axios.put(`${API}/bible-quotes/${editing}`, form);
        toast.success('Quote updated');
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (q) => {
    try {
      await axios.put(`${API}/bible-quotes/${q.id}/toggle`);
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete "${q.reference}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/bible-quotes/${q.id}`);
      toast.success('Quote deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const activeCount = quotes.filter(q => q.is_active).length;

  return (
    <div className="space-y-5" data-testid="bible-quotes-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            Bible Quotes
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Verses that auto-rotate as filler slides between rules on your TV. {activeCount} active · {quotes.length} total.
          </p>
        </div>
        <button
          onClick={startNew}
          disabled={editing === 'new'}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold shadow hover:shadow-lg disabled:opacity-50"
          data-testid="bible-quote-add-btn"
        >
          <Plus className="w-4 h-4" /> Add Quote
        </button>
      </div>

      {editing === 'new' && (
        <QuoteEditor form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} title="New Bible Quote" />
      )}

      {loading ? (
        <div className="text-center py-10 text-slate-500">Loading quotes…</div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No Bible quotes yet. Add your first one to start the rotation.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {quotes.map(q => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`relative rounded-xl border-2 p-4 transition-colors ${
                q.is_active
                  ? 'bg-white border-violet-200 hover:border-violet-400'
                  : 'bg-slate-50 border-slate-200 opacity-70'
              }`}
            >
              {editing === q.id ? (
                <QuoteEditor form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} title="Edit Quote" />
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="font-bold text-violet-700">{q.reference}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">{q.category || 'Inspiration'}</span>
                      {!q.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium">Inactive</span>}
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed italic">"{q.text}"</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => toggle(q)}
                      title={q.is_active ? 'Disable' : 'Enable'}
                      className={`p-2 rounded-lg transition-colors ${q.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'}`}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => startEdit(q)}
                      title="Edit"
                      className="p-2 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(q)}
                      title="Delete"
                      className="p-2 rounded-lg bg-rose-100 text-rose-700 hover:bg-rose-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

const QuoteEditor = ({ form, setForm, onSave, onCancel, saving, title }) => (
  <div className="space-y-3 bg-violet-50/40 border border-violet-200 rounded-xl p-4">
    <h3 className="font-bold text-slate-800">{title}</h3>
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-semibold text-slate-600">Reference</label>
        <input
          type="text"
          placeholder="e.g. John 3:16"
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-violet-400 outline-none text-sm"
          data-testid="bible-quote-reference"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Category</label>
        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-violet-400 outline-none text-sm bg-white"
          data-testid="bible-quote-category"
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </div>
    <div>
      <label className="text-xs font-semibold text-slate-600">Verse Text</label>
      <textarea
        rows={3}
        placeholder="Type the verse text…"
        value={form.text}
        onChange={(e) => setForm({ ...form, text: e.target.value })}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-violet-400 outline-none text-sm leading-relaxed"
        data-testid="bible-quote-text"
      />
    </div>
    <div className="flex items-center justify-between">
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="w-4 h-4 rounded text-violet-600"
        />
        Active (show on TV rotation)
      </label>
      <div className="flex gap-2">
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm font-medium">
          <X className="w-4 h-4" /> Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="bible-quote-save"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  </div>
);

export default BibleQuotesModule;
